import React from 'react';
import { Link, LocaleAnchor } from '@/i18n/router';
import { useLocale, useMessages } from '@/i18n/LocaleProvider';
import messages from '@/i18n/catalogs/corporate/HeroSection';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check } from 'lucide-react';
import { CLOUD_LAUNCH_PROMO, CLOUD_PRICING_TIERS, CLOUD_SIGNUP_URL } from '@/data/cloud';
import HardchargerArt from '@/components/hardcharger/HardchargerArt';
import TpcDisclaimer from '@/components/TpcDisclaimer';

const HeroSection = React.memo(() => {
  const m = useMessages(messages);
  const { locale, formatLocale } = useLocale();
  // English keeps the price strings from src/data/cloud.ts as written; Portuguese formats the same USD amounts.
  const usd = (amount: number, english: string) => locale === 'en' ? english
    : new Intl.NumberFormat(formatLocale, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  const firstPlan = CLOUD_PRICING_TIERS[0].price;
  // Hardcharger launch. Benchmark: src/data/hardcharger.ts TPCH_SF1000 (rc.1; caveats on /hardcharger#performance);
  // pricing: CLOUD_PRICING_TIERS.
  const cloudPoints = [
    m.point1,
    `${m.point2Start}${usd(Number(firstPlan.replace(/[$,]/g, '')), firstPlan)}${m.point2End}`,
    m.point3,
    m.point4,
  ];
  return (
    <header className="hc-band text-white relative overflow-hidden" role="banner">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-hc-azure/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] bg-hc-scarlet/10 rounded-full blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 animate-fade-in">
            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-full text-sm font-medium">
                <span className="h-2 w-2 animate-pulse rounded-full bg-hc-gold" />
                {m.s4}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl xl:text-[3.4rem] font-bold leading-tight">
              {m.titleStart}<span className="text-hc-azure">{m.titleHighlight}</span>.
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              {m.subtitle}
            </p>

            <div className="flex flex-wrap gap-4">
              <LocaleAnchor href={CLOUD_SIGNUP_URL}>
                <Button
                  size="lg"
                  className="bg-white text-hc-navy hover:bg-hc-paper dark:bg-white dark:text-hc-navy dark:hover:bg-hc-paper font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  data-ga-cta="cloud_signup"
                  data-ga-location="home_hero"
                >
                  {m.s11}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </LocaleAnchor>
              <Link to="/hardcharger">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-white text-white hover:bg-white/10 hover:text-white dark:bg-transparent dark:border-white dark:text-white dark:hover:bg-white/10 dark:hover:text-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent transition-all"
                  data-ga-cta="explore_hardcharger"
                  data-ga-location="home_hero"
                >
                  {m.s12}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <ul className="space-y-2">
              {cloudPoints.map((point) => (
                <li key={point} className="flex items-start gap-2 text-gray-200">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-hc-azure" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <p className="text-sm text-gray-300">
              {CLOUD_LAUNCH_PROMO.active && (
                <>
                  {m.launchOfferStart}{usd(CLOUD_LAUNCH_PROMO.creditUsd, `$${CLOUD_LAUNCH_PROMO.creditUsd}`)}{m.launchOfferEnd}
                </>
              )}
              {m.s16}
              <Link
                to="/hardcharger/install"
                className="underline underline-offset-4 hover:text-white"
                data-ga-cta="install_hardcharger"
                data-ga-location="home_hero"
              >
                {m.s17}
              </Link>
              .
            </p>

            {/* Trust row */}
            <p className="text-sm text-gray-400 pt-1">{m.s18}</p>
            <TpcDisclaimer deviations={false} className="text-gray-400" />
          </div>

          {/* The bull: the page's LCP image, so it is not animated. The band is dark in both themes. */}
          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div
              className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-hc-cobalt/30 via-hc-azure/10 to-hc-scarlet/15 blur-3xl"
              aria-hidden="true"
            />
            <Link to="/hardcharger" className="relative block transition-transform hover:-translate-y-1" data-ga-cta="explore_hardcharger" data-ga-location="home_hero_art" data-ga-text="Hardcharger bull">
              <HardchargerArt variant="dark" priority alt={m.s20} sizes="(min-width: 1280px) 600px, (min-width: 1024px) 47vw, (min-width: 640px) 28rem, calc(100vw - 2rem)" />
            </Link>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" aria-hidden="true"></div>
    </header>
  );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;
